import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Shield, Mail, ExternalLink, ArrowUpRight } from 'lucide-react';

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const linkClass =
    'text-xs font-mono uppercase tracking-[0.14em] text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 group';

  return (
    <footer
      className="relative border-t border-border bg-muted/30"
      data-testid="footer"
    >
      {/* Thin top accent strip */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative bg-primary/10 border border-primary/40 p-2 sharp">
                <Shield className="h-5 w-5 text-primary" strokeWidth={2.5} />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-base font-bold tracking-tight text-foreground">
                  Threat.live
                </span>
                <span className="mt-1 text-[9px] font-mono text-muted-foreground tracking-[0.22em] uppercase">
                  Threat Intelligence Platform
                </span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
              Advanced real-time threat detection and BGP-based blackhole routing,
              built to defend networks through collective cybersecurity intelligence.
            </p>

            {/* Status strip */}
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 border border-primary/30 bg-primary/5 sharp">
              <span className="status-dot" />
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-primary">
                All Systems Operational
              </span>
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-4">
            <h4 className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-4 pb-2 border-b border-border">
              // Navigation
            </h4>
            <ul className="space-y-3">
              {[
                { href: '/ip-list', label: t('ipList'), internal: true },
                { href: '/setup', label: t('setupGuide'), internal: true },
                { href: '/sponsors', label: language === 'tr' ? 'Sponsorlar' : 'Sponsors', internal: true },
                { href: '/tools', label: t('tools'), internal: true },
                { href: '/login', label: t('portal'), internal: true },
              ].map((link) => (
                <li key={link.href}>
                  {link.internal ? (
                    <Link
                      to={link.href}
                      className={linkClass}
                    >
                      <span className="text-primary/50 group-hover:text-primary">→</span>
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClass}
                    >
                      <span className="text-primary/50 group-hover:text-primary">→</span>
                      {link.label}
                      <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h4 className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-4 pb-2 border-b border-border">
              // {t('contact')}
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:contact@threat.live"
                  className={linkClass}
                >
                  <Mail className="h-3.5 w-3.5" />
                  {/* '@' ayrı span: Cloudflare e-posta gizleme metni değiştirip hydration'ı bozmasın */}
                  <span>contact<span>@</span>threat.live</span>
                </a>
              </li>
            </ul>

            {/* Fingerprint (pure vibe) */}
            <div className="mt-6 p-3 border border-border bg-background/50 sharp">
              <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1">
                Node Fingerprint
              </div>
              <div className="text-[11px] font-mono text-foreground/80 break-all">
                SHA256:a1f3…9c4d
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-border">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
              <p className="text-xs font-mono uppercase tracking-[0.14em] text-muted-foreground">
                © {currentYear} Threat.live · {t('allRightsReserved')}
              </p>
              <span className="hidden md:inline text-border">|</span>
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                v2.1 · build <span className="text-foreground">a1f3e9</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                {language === 'tr' ? 'Geliştiren' : 'Developed by'} //
              </span>
              <a
                href="https://www.linkedin.com/in/dogussekerci/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] font-mono uppercase tracking-[0.14em] text-foreground hover:text-primary transition-colors flex items-center gap-1"
              >
                @dogussekerci
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
